"use client"

import { useEffect, useRef } from "react"

import { type Recipe, paintRecipe } from "@/lib/cover-press/engine"

interface PressCanvasProps {
  recipe: Recipe
  /** Pixel size of the pull. The canvas is scaled to fit by CSS. */
  width: number
  height: number
  /** Apply the format's post-process (disc mask, card frame). */
  post?: boolean
  /** Delay the repaint; the hero paints on the next frame, the rest wait. */
  delayMs?: number
  className?: string
  style?: React.CSSProperties
  /** A decorative pull inside a labelled control needs no name of its own. */
  "aria-hidden"?: boolean
  role?: string
  "aria-label"?: string
}

/**
 * A canvas that repaints whenever its recipe changes. The paint is CPU-bound,
 * so it runs behind a frame (hero) or a short timer (secondary pulls) rather
 * than inside render, and a superseded paint is cancelled before it starts.
 */
export function PressCanvas({
  recipe,
  width,
  height,
  post = false,
  delayMs = 0,
  className,
  style,
  ...rest
}: PressCanvasProps) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const cv = ref.current
    if (!cv) return
    let raf = 0
    const timer = setTimeout(() => {
      raf = requestAnimationFrame(() => {
        paintRecipe(cv, recipe, width, height, post)
      })
    }, delayMs)
    return () => {
      clearTimeout(timer)
      cancelAnimationFrame(raf)
    }
  }, [recipe, width, height, post, delayMs])

  return (
    <canvas
      ref={ref}
      width={width}
      height={height}
      className={className}
      style={style}
      {...rest}
    />
  )
}
